import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  getModuleKeyFromPath,
  getPagination,
  setPagination,
} from "../services/paginationStorage";

export function usePersistedPagination({
  paginationKey,
  persistPagination = true,
  defaultPageSize = 20,
} = {}) {
  const { pathname } = useLocation();
  const moduleKey = persistPagination
    ? paginationKey || getModuleKeyFromPath(pathname)
    : null;

  const [paginationModel, setPaginationModel] = useState(() => {
    const stored = moduleKey ? getPagination(moduleKey) : null;
    return stored || { page: 0, pageSize: defaultPageSize };
  });

  useEffect(() => {
    if (!moduleKey) return;
    setPagination(
      moduleKey,
      paginationModel.page + 1,
      paginationModel.pageSize
    );
  }, [moduleKey, paginationModel.page, paginationModel.pageSize]);

  return [paginationModel, setPaginationModel];
}

export function useAgGridPersistedPagination({
  paginationKey,
  persistPagination = true,
  defaultPageSize = 20,
} = {}) {
  const { pathname } = useLocation();
  const moduleKey = persistPagination
    ? paginationKey || getModuleKeyFromPath(pathname)
    : null;
  const stored = moduleKey ? getPagination(moduleKey) : null;
  const paginationPageSize = stored?.pageSize ?? defaultPageSize;
  const restoredPage = stored?.page ?? 0;
  const readyToPersist = useRef(restoredPage === 0);

  const restorePage = useCallback(
    (api) => {
      if (api && restoredPage > 0) {
        api.paginationGoToPage(restoredPage);
      }
      readyToPersist.current = true;
    },
    [restoredPage]
  );

  const onPaginationChanged = useCallback(
    (event) => {
      if (!readyToPersist.current || !moduleKey || !event?.api) return;
      setPagination(
        moduleKey,
        event.api.paginationGetCurrentPage() + 1,
        event.api.paginationGetPageSize()
      );
    },
    [moduleKey]
  );

  return { paginationPageSize, onPaginationChanged, restorePage };
}
